import { GetStaticProps, NextPage } from "next"
import Image from "next/image"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { rgba } from "polished"
import React, { useMemo } from "react"
import { StructuredText } from "react-datocms"
import { Col, Row } from "react-styled-flexboxgrid"
import styled from "styled-components"

import { BlogsSection } from "../components/blogs-section/BlogsSection"
import { Card } from "../components/card/Card"
import { CardHeader } from "../components/card/components/CardHeader"
import { CardContent } from "../components/card/components/CardContent"
import { GetStartedSection } from "../components/get-started-section/GetStartedSection"
import { Heading } from "../components/heading/Heading"
import { Icon } from "../components/icon/Icon"

import { MainLayout } from "../components/layouts/main-layout/MainLayout"
import { NextStepsSection } from "../components/next-steps-section/NextStepsSection"
import { Section } from "../components/section/Section"
import { Paragraph } from "../components/paragraph/Paragraph"
import { SingleColLayout } from "../components/single-col-layout/SingleColLayout"
import { TwoColLayout } from "../components/two-col-layout/TwoColLayout"
import { HeroBlock } from "../datocms/components/HeroBlock"
import { nextLocaleToDatoLocale } from "../datocms/dato-utils"
import { datoClient } from "../datocms/gql/client/apollo-client"
import {
  GetFrontPageDocument,
  GetFrontPageQuery,
  GetFrontPageQueryVariables,
  ImgixParamsAuto,
  ImgixParamsFit,
} from "../datocms/gql/generated/graphql"
import {
  omitParagraphTagRenderRule,
  paragraphRenderRule,
} from "../datocms/nodeRules"
import { BasePageProps, NEXT_JS_ISR_REVALIDATE } from "../util/page"
import { BrandIcon, theme } from "../util/styles/theme"
import { getFrontPageSectionProps } from "../util/styles/common-section-props"

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const response = await datoClient(preview).query<
    GetFrontPageQuery,
    GetFrontPageQueryVariables
  >({
    query: GetFrontPageDocument,
    variables: {
      imgixParams: {
        auto: [ImgixParamsAuto.format],
        fit: ImgixParamsFit.crop,
        h: 410,
        w: 654,
      },
      locale: nextLocaleToDatoLocale(locale),
    },
  })

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      blogPosts: response.data.allArticles,
      footer: response.data.footer,
      frontPage: response.data.frontPage,
      titleSuffix: response.data.site.globalSeo.titleSuffix,
    },
    revalidate: NEXT_JS_ISR_REVALIDATE,
  }
}

const ImageContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;

  margin-bottom: ${theme.spacings.xlarge};
  padding: 1rem;

  ${theme.breakpoints.md} {
    margin-bottom: 0;
    padding: 5rem;
  }
`

const FeaturesIntroLayout = styled(SingleColLayout)`
  margin-bottom: ${theme.spacings.xlarge};
  ${theme.breakpoints.md} {
    margin-bottom: calc(2 * ${theme.spacings.xlarge});
  }
`

const ImageInnerContainer = styled.div`
  border-radius: 50%;
  box-shadow: 0 0 2rem 0 ${rgba(theme.colors.orange, 0.5)};
`

const CardCol = styled(Col)`
  display: flex;

  div {
    flex-grow: 1;
  }
`

const StyledIcon = styled(Icon)`
  ${theme.textStyle("heading", "xxlarge", "medium")};

  ${theme.breakpoints.sm} {
    ${theme.textStyle("heading", "xlarge", "medium")};
  }

  margin-left: auto;
  margin-right: auto;
  text-align: center;
`

type HomeProps = {
  blogPosts: GetFrontPageQuery["allArticles"]
  frontPage: GetFrontPageQuery["frontPage"]
  titleSuffix: string
} & BasePageProps

const Home: NextPage<HomeProps> = ({
  blogPosts,
  frontPage,
  titleSuffix,
  footer,
}) => {
  const heroModel = frontPage.hero[0]
  const hero = heroModel && <HeroBlock model={heroModel} />
  const blogsSection = frontPage.blogs[0]
  const intro = frontPage.intro[0]
  const featuresIntro = frontPage.featuresIntro[0]
  const featuresSection = frontPage.features[0]

  const showBlogs = blogPosts?.length > 1
  const {
    introProps,
    featuresProps,
    blogsProps,
    getStartedProps,
  } = getFrontPageSectionProps(showBlogs)

  const introRenderNodeRules = useMemo(() => {
    return [paragraphRenderRule({ size: "xlarge" })]
  }, [])

  return (
    <MainLayout
      type="metaTags"
      heroComponent={hero}
      topBackground="white"
      bottomBackground="yellow"
      titleSuffix={titleSuffix}
      metaTags={frontPage.seoMetaTags}
      footer={footer}
    >
      {intro && (
        <Section
          backgroundColor={introProps.backgroundColor}
          bottomCut={introProps.bottomCut}
        >
          <TwoColLayout>
            {{
              firstCol: (
                <Heading as="h2" size="xlarge" color="orange">
                  <StructuredText
                    data={intro.header.value}
                    customNodeRules={[omitParagraphTagRenderRule]}
                  />
                </Heading>
              ),
              secondCol: (
                <StructuredText
                  data={intro.text.value}
                  customNodeRules={introRenderNodeRules}
                />
              ),
            }}
          </TwoColLayout>
        </Section>
      )}
      {showBlogs && (
        <BlogsSection
          blogSection={blogsSection}
          sectionProps={blogsProps}
          blogPosts={blogPosts}
        />
      )}
      <Section
        backgroundColor={featuresProps.backgroundColor}
        bottomCut={featuresProps.bottomCut}
      >
        <FeaturesIntroLayout colProps={{ xs: 12, sm: 6, smOffset: 3 }}>
          <Heading as="h3" size="xlarge" color="blue" align="center">
            <StructuredText
              data={featuresIntro.header.value}
              customNodeRules={[omitParagraphTagRenderRule]}
            />
          </Heading>
          <StructuredText
            data={featuresIntro.text.value}
            customNodeRules={introRenderNodeRules}
          />
        </FeaturesIntroLayout>
        <TwoColLayout
          firstColProps={{
            xs: 8,
            xsOffset: 2,
            sm: 6,
            smOffset: 3,
            md: 6,
            mdOffset: 0,
          }}
        >
          {{
            firstCol: (
              <ImageContainer>
                <ImageInnerContainer>
                  <Image
                    src="/aisti-radar.svg"
                    alt=""
                    layout="responsive"
                    width={568}
                    height={568}
                  />
                </ImageInnerContainer>
              </ImageContainer>
            ),
            secondCol: (
              <Row>
                {featuresSection.cards.map(fc => {
                  return (
                    <CardCol xs={6} key={fc.id}>
                      <Card bottomMargin>
                        <CardHeader>
                          <StyledIcon
                            icon={fc.icon.name as BrandIcon}
                            color="orange"
                          />
                        </CardHeader>
                        <CardContent>
                          <Paragraph align="center">
                            <StructuredText
                              data={fc.title.value}
                              customNodeRules={[omitParagraphTagRenderRule]}
                            />
                          </Paragraph>
                        </CardContent>
                      </Card>
                    </CardCol>
                  )
                })}
              </Row>
            ),
          }}
        </TwoColLayout>
      </Section>
      <GetStartedSection sectionProps={getStartedProps} variant="tabbed" />
      <NextStepsSection />
    </MainLayout>
  )
}

export default Home
